if (!customElements.get('sande-comparison')) {
  customElements.define('sande-comparison', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const panel = this.querySelector('.sande-compare-panel');
      if (!panel || typeof HTMLDialogElement === 'undefined') return;
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'sande-compare-dialog';
      this.dialog.setAttribute('aria-labelledby', panel.querySelector('h2').id);
      this.append(this.dialog);
      this.dialog.append(panel);
      panel.querySelector('[data-compare-close]').hidden = false;
      this.cards = [...this.querySelectorAll('[data-compare-card]')];
      this.track = this.querySelector('.sande-compare-grid');
      this.progress = this.querySelector('.sande-compare-progress span');
      this.updateProgress = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const ratio = max > 0 ? this.track.scrollLeft / max : 0;
        this.progress.style.width = `${100 / Math.max(1, this.cards.length)}%`;
        this.progress.style.transform = `translateX(${ratio * (this.cards.length - 1) * 100}%)`;
      };
      this.track.addEventListener('scroll', this.updateProgress, { ...options, passive: true });
      this.resizeObserver = new ResizeObserver(this.updateProgress);
      this.resizeObserver.observe(this.track);
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const step = this.cards[1] ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.track.clientWidth;
        this.track.scrollBy({ left: step * (event.key === 'ArrowRight' ? 1 : -1), behavior: 'instant' });
      }, options);
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-sande-compare-trigger], [data-compare-open]');
        if (!trigger || (trigger.hasAttribute('data-compare-open') && !this.contains(trigger))) return;
        event.preventDefault();
        if (this.dialog.open) return;
        this.opener = trigger;
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
        this.updateProgress();
        this.dialog.querySelector('[data-compare-close]').focus();
      }, options);
      this.querySelector('[data-compare-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, options);
      this.cards.forEach(card => {
        this.setupGallery(card);
        card.addEventListener('click', event => {
          const swatch = event.target.closest('[data-sibling-id]');
          if (!swatch) return;
          const template = card.querySelector(`[data-sibling-template="${swatch.dataset.siblingId}"]`);
          if (!template) return;
          event.preventDefault();
          card.querySelector('[data-sibling-display]').replaceChildren(template.content.cloneNode(true));
          card.querySelector('[data-compare-shop]').href = template.dataset.url;
          card.toggleAttribute('data-current-product', template.dataset.current === 'true');
          card.querySelectorAll('[data-sibling-id]').forEach(item => {
            if (item === swatch) item.setAttribute('aria-current', 'true');
            else item.removeAttribute('aria-current');
          });
          this.setupGallery(card);
        }, options);
      });
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        this.querySelector('[data-compare-open]').click();
        const card = event.target.closest('[data-compare-card]');
        if (card) this.track.scrollTo({ left: this.track.scrollLeft + card.getBoundingClientRect().left - this.track.getBoundingClientRect().left - parseFloat(getComputedStyle(this.track).paddingLeft) });
      }, options);
    }
    setupGallery(card) {
      this.galleryControllers ||= new Map();
      this.galleryControllers.get(card)?.abort();
      const controller = new AbortController();
      this.galleryControllers.set(card, controller);
      const options = { signal: controller.signal };
      const gallery = card.querySelector('.sande-card-gallery');
      const indicators = card.querySelector('.sande-image-indicators');
      if (!indicators) return;
      indicators.hidden = false;
      const bars = [...indicators.children];
      const goTo = index => gallery.scrollTo({ left: Math.max(0, Math.min(1, index)) * gallery.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      gallery.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const current = Math.round(gallery.scrollLeft / gallery.clientWidth);
        goTo(event.key === 'Home' ? 0 : event.key === 'End' ? 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      let drag;
      gallery.addEventListener('dragstart', event => event.preventDefault(), options);
      gallery.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { x: event.clientX, scroll: gallery.scrollLeft, index: Math.round(gallery.scrollLeft / gallery.clientWidth) };
        gallery.setPointerCapture(event.pointerId);
        gallery.classList.add('is-dragging');
      }, options);
      gallery.addEventListener('pointermove', event => {
        if (drag) gallery.scrollLeft = drag.scroll + drag.x - event.clientX;
      }, options);
      const finishDrag = event => {
        if (!drag) return;
        const distance = drag.x - event.clientX;
        const target = Math.abs(distance) > 30 ? drag.index + Math.sign(distance) : drag.index;
        drag = null;
        gallery.classList.remove('is-dragging');
        if (gallery.hasPointerCapture(event.pointerId)) gallery.releasePointerCapture(event.pointerId);
        goTo(target);
      };
      gallery.addEventListener('pointerup', finishDrag, options);
      gallery.addEventListener('pointercancel', finishDrag, options);
      gallery.addEventListener('scroll', () => {
        const index = Math.round(Math.abs(gallery.scrollLeft) / gallery.clientWidth);
        bars.forEach((bar, i) => bar.classList.toggle('is-active', i === index));
      }, { ...options, passive: true });
    }
    disconnectedCallback() {
      if (this.dialog?.open) document.documentElement.style.overflow = this.previousOverflow || '';
      this.resizeObserver?.disconnect();
      this.galleryControllers?.forEach(controller => controller.abort());
      this.controller?.abort();
      this.controller = null;
    }
  });
}
